import { useState } from "react";
import { Link } from "react-router-dom";
import { formatDate } from "../api";
import { useAuth } from "../auth";
import { useApi } from "../hooks";
import { StatusBadge } from "./Ui";
import { ActionForm, RequestView, useMutation, type RecordData } from "./Workflow";

type Client = { id: number; name: string; status?: string };

/**
 * The NL-AOS tracking script: sites, their one-line snippets and last-7-day counts.
 * With `clientId` it shows that client's sites (client Tracking page, anchored at
 * #tracking-script so Website analytics can link straight to it); without it,
 * every client's, with a client picker for new sites (Settings). With
 * `foldedTitle` the panel starts folded under that title (clients whose own site
 * takes no scripts: it only applies to GHL pages NOLA runs for them).
 */
export default function TrackingSites({ clientId, foldedTitle, defaultOpen = false }: { clientId?: number; foldedTitle?: string; defaultOpen?: boolean }) {
  const { user } = useAuth();
  const canEdit = user?.role === "superadmin" || user?.role === "exec";
  const sites = useApi<{ sites: RecordData[] }>(clientId ? `/api/tracking/${clientId}/sites` : "/api/tracking/sites");
  const clients = useApi<Client[]>(clientId ? null : "/api/clients");
  const mutation = useMutation();
  const [created, setCreated] = useState<RecordData | null>(null);

  const addSite = async (data: RecordData) => {
    const target = clientId ?? Number(data.client_id);
    if (!target) return mutation.setNotice("Choose the client these pages belong to.");
    const r = await mutation.run<RecordData>(`/api/tracking/${target}/sites`, { label: data.label, domains: data.domains });
    if (r) {
      setCreated(r);
      sites.reload();
    }
  };
  const toggle = async (id: number, active: boolean) => {
    const r = await mutation.run(`/api/tracking/sites/${id}`, { active }, "PUT");
    if (r) sites.reload();
  };
  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      mutation.setNotice("Snippet copied. Paste it into the funnel header.");
    } catch {
      mutation.setNotice("Copy failed. Select the snippet and copy it by hand.");
    }
  };

  const list = sites.data?.sites || [];
  const body = (
    <>
      <ol className="tracking-install">
        <li>
          <strong>Create a site</strong>
          <span>List the domains the funnel is served from. Each site gets its own key.</span>
        </li>
        <li>
          <strong>Paste the snippet</strong>
          <span>In the GHL funnel: <b>Settings → Tracking code → Header</b>, then save and publish.</span>
        </li>
        <li>
          <strong>Test it</strong>
          <span>Open the funnel with <code>?gclid=test123</code> on the end. A page view from a Google Ads click shows below within seconds.</span>
        </li>
      </ol>
      <details className="tracking-howto">
        <summary>How the script works, and what it cannot see</summary>
        <p className="workflow-note">
          It keeps the Google click ID and UTM tags for 90 days, fills matching
          hidden form fields, and sends each form submission and phone tap to
          NOLA, where it joins job matching. It only accepts events from the
          domains listed for the site. Never install it on a client's own
          website without their written OK.
        </p>
        <p className="workflow-note">
          A GHL form on the funnel is an embed served from GHL's own servers, so
          the script cannot read what a visitor types into it. Those leads arrive
          through the GHL contact sync with the click ID GHL recorded, and are
          credited to the funnel visit that carried the same click ID. The script
          still records the page view, the click ID, phone taps, and any form
          built on the page itself.
        </p>
        <p className="workflow-note">
          Track anything else with <code>nlaTrack('quote_started')</code> after the script loads.
        </p>
      </details>
      {mutation.feedback}
      {created && (
        <div className="tracking-site tracking-site-new">
          <strong>Site created. Paste this into the funnel header:</strong>
          <div className="tracking-snippet">
            <code>{created.snippet}</code>
            <button className="button" onClick={() => void copy(created.snippet)}>Copy</button>
          </div>
        </div>
      )}
      <RequestView request={sites}>
        {list.map((site) => (
          <div key={site.id} className={`tracking-site${site.active ? "" : " is-off"}`}>
            <div className="tracking-site-head">
              <div>
                <span className="tracking-site-title">
                  <strong>{clientId ? site.label || "Tracking site" : `${site.client_name}${site.label ? `: ${site.label}` : ""}`}</strong>
                  <StatusBadge tone={!site.active ? "neutral" : site.last_event_at ? "success" : "warning"}>
                    {!site.active ? "Switched off" : site.last_event_at ? "Live" : "No events yet"}
                  </StatusBadge>
                </span>
                <span>
                  {(site.domains || []).join(", ")}
                  {site.active && site.last_event_at ? `, last event ${formatDate(site.last_event_at)}` : ""}
                </span>
              </div>
              <div className="tracking-site-actions">
                <Link className="button secondary" to={`/website?client_id=${site.client_id}&site=${site.id}`}>Open analytics</Link>
                {canEdit && (
                  <button className="button secondary" disabled={mutation.busy} onClick={() => void toggle(site.id, !site.active)}>
                    {site.active ? "Switch off" : "Switch on"}
                  </button>
                )}
              </div>
            </div>
            <div className="tracking-snippet">
              <code>{site.snippet}</code>
              <button className="button secondary" onClick={() => void copy(site.snippet)}>Copy</button>
            </div>
            <dl className="tracking-site-stats" aria-label="Last 7 days">
              <div><dt>Page views</dt><dd>{Number(site.page_views_7d).toLocaleString()}<small>7 days</small></dd></div>
              <div><dt>From Google Ads</dt><dd>{Number(site.google_clicks_7d).toLocaleString()}<small>clicks</small></dd></div>
              <div><dt>Form leads</dt><dd>{Number(site.leads_7d).toLocaleString()}</dd></div>
              <div><dt>Phone taps</dt><dd>{Number(site.call_taps_7d).toLocaleString()}</dd></div>
            </dl>
          </div>
        ))}
        {!list.length && <p className="tracking-sites-empty">No sites yet. Create the first one below and paste its snippet into the funnel header.</p>}
      </RequestView>
      {canEdit && (
        <div className={`workflow-panel inline-form ${clientId ? "inline-form-2" : "inline-form-3"}`}>
          <h3 className="inline-form-title">Add a site</h3>
          <ActionForm
            key={created?.id ?? "new"}
            submit="Create site"
            busy={mutation.busy}
            onSubmit={addSite}
            fields={[
              ...(clientId
                ? []
                : [{
                    name: "client_id",
                    label: "Client",
                    required: true,
                    options: [
                      { value: "", label: clients.loading ? "Loading clients…" : "Choose a client" },
                      ...(clients.data || []).filter((c) => c.status !== "churned").map((c) => ({ value: String(c.id), label: c.name })),
                    ],
                  }]),
              { name: "label", label: "Name", placeholder: "Junk removal funnel" },
              { name: "domains", label: "Funnel domains", required: true, placeholder: "offer.clientsite.com", hint: "Comma separated. Subdomains of a listed domain are accepted too." },
            ]}
          />
        </div>
      )}
    </>
  );

  if (foldedTitle) {
    return (
      <details className="panel tracking-fold" id={clientId ? "tracking-script" : undefined} open={defaultOpen || undefined}>
        <summary>{foldedTitle}</summary>
        {body}
      </details>
    );
  }
  return (
    <section className="panel" id={clientId ? "tracking-script" : undefined} aria-labelledby={clientId ? "tracking-script-title" : undefined}>
      <div className="panel-heading">
        <div>
          <h2 id={clientId ? "tracking-script-title" : undefined}>Tracking script</h2>
        </div>
      </div>
      {body}
    </section>
  );
}
